import { create } from 'zustand';
import {
    ConfigOption,
    ProductsData,
    ProductsDataDetail,
    ProductsDataKeys,
    activeProductDataType,
    exportedData,
    inforStepValues,
    inforTab,
    monogram,
    products,
    stepSize,
    unitData,
} from './types';
import {
    deepCopy,
    delay,
    generateFabricId,
    getConfigName,
    getImagePath,
    getProcedures,
    resizeBase64Img,
} from './utils';
import {
    DEFAULT_FABRIC,
    DEFAULT_FABRIC_ID,
    DEFAULT_FABRIC_OBJ,
    DEFAULT_INNER_IMAGE,
    DEFAULT_INNER_VALUE,
    DEFAULT_STITCH,
    DEFAULT_STITCH_NAME,
    INIT_PRODUCT_DATA,
} from './constants';

type State = {
    selectingApplyAllFabric: string;
    changeSelectingApplyAllFabric: (state: string) => void;
    isShowSelectedFabric: boolean;
    changeShowSelectedFabric: (state: boolean) => void;
    inforFlow: inforTab;
    changeInforFlow: (tab: inforTab) => void;
    inforStep: number;
    changeInforStep: (step: number) => void;
    isAddMoreShirt: boolean;
    setAddMoreShirt: (isAddMoreShirt: boolean) => void;
    inforFormState: boolean;
    changeInforFormState: (state: boolean) => void;
    currentConfig: string;
    setCurrentConfig: (currentConfig: string) => void;
    activeFabric: string;
    setActiveFabrics: (activeFabric: string) => void;
    activeProductData: activeProductDataType;
    // change option
    setProductFabric: (
        activeFabric: ProductsDataDetail,
        type?: ProductsDataKeys,
        img?: string
    ) => void;
    setStitchApply: (state?: boolean) => void;
    applyAllCurrentOptions: (fabricId: string) => void;
    runProcedure: (
        procedureName: string,
        item: ConfigOption,
        type?: ProductsDataKeys,
        src?: string
    ) => void;
    addNewProduct: () => void;
    isLoading: boolean;
    setIsLoading: (state: boolean) => void;
    submitData: () => void;
    changeTab: (tab: string) => void;
    resetToDefault: () => void;
    setMonogram: (type: string, value: string) => void;
    setInnerFabric: (type: string, value?: string, img?: string) => void;
    setStitch: (id: string, value: string) => void;
    setElbowPatch: () => void;
    isApplyForAll: boolean;
    removeSelectedFabric: (fabricId: string) => void;
    changeApplyForAll: (state: boolean) => void;
    fabricApplied: string; // current fabric (apply for all)
    changeFabricApplied: (fabric: string) => void; // change active applied fabric (apply for all)
    inforStepValues: inforStepValues;
    changeInforStepValues: (
        step: string,
        childState: string | { [key in stepSize]: string },
        childStep?: string
    ) => void;

    inforInputValues: { [key: string]: string };
    changeInforInputValues: (id: string, value: string) => void;
};

const useStateStore = create<State>((set, get) => ({
    submitData: async () => {
        try {
            set({ isLoading: true });
            const procedures = getProcedures();
            const configurator = get().activeProductData;
            const inputs = get().inforInputValues;
            const steps = get().inforStepValues;
            const option = get().inforFlow;

            const exportedData: exportedData = {
                products: {},
                option,
                steps,
                inputs,
            };

            const products: products = {};
            let index = 1;
            const screenshots: string[] = [];
            const fabricIds = Object.keys(configurator);

            for (const fabricId of fabricIds) {
                if (procedures?.takeScreenShot && procedures?.getScreenShot) {
                    get().applyAllCurrentOptions(fabricId);
                    procedures.takeScreenShot();
                    setTimeout(async () => {
                        const base64Img = procedures.getScreenShot();
                        const resiedBase64String = await resizeBase64Img(base64Img, 300, 200);
                        if (resiedBase64String) {
                            screenshots.push(resiedBase64String);
                        }
                    }, 700); // delay for taking screenshot -> then download captured image
                }
                await delay(800);
            }
            get().applyAllCurrentOptions(get().activeFabric);

            Object.keys(configurator).forEach((key) => {
                const fabricKey = 'fabric-' + index.toString();
                const currentFabric = configurator[key];
                let fabric = {};
                Object.keys(currentFabric).forEach((key) => {
                    const curItem = currentFabric[key as ProductsDataKeys];
                    let data: unitData = curItem.value;
                    if (key === 'cuff') {
                        data = {
                            value: curItem.value,
                            elbowPatch: curItem.elbowPatch || false,
                        };
                    } else if (key === 'stitch') {
                        data = {
                            value: curItem.value,
                            lastButtonOnly: curItem.theLastOne || false,
                        };
                    } else if (key === 'inner-fabric') {
                    } else if (key === 'monogram') {
                        let monogramObject: monogram = {};
                        const placements = currentFabric.monogram.placements!;
                        placements &&
                            Object.keys(placements).forEach((key) => {
                                monogramObject = {
                                    ...monogramObject,
                                    [key]: placements[key].isVisible
                                        ? placements[key].text
                                            ? placements[key].text
                                            : placements[key]['style-id']
                                        : '',
                                };
                            });
                        fabric = {
                            ...fabric, //previous looped data
                            monogram: monogramObject,
                        };
                    } else if (key === 'fabric') {
                        data = {
                            value: curItem.value,
                            price: curItem.price,
                            name: curItem.name,
                        };
                    }

                    fabric = {
                        ...fabric,
                        ...(key !== 'monogram' && { [key]: data }),
                    };
                });

                products[fabricKey] = fabric;
                //@ts-ignore
                products[fabricKey].img = screenshots[index - 1];
                index++;
            });
            exportedData.products = products;

            window.parent.postMessage(
                {
                    event: 'add_product_to_cart',
                    data: exportedData,
                },
                '*'
            );

            set({ isLoading: false, inforFormState: false });
        } catch (error) {
            console.log(error);
            set({ isLoading: false, inforFormState: false });
        }
    },
    inforStepValues: {
        size: {
            age: '18',
            height: '180',
            weight: '85',
            'shoe-size': '12',
        },
        'body-shape': 'rectangle',
        'shoulder-shape': 'straight',
        'collar-size': '',
        'in-or-out': 'both',
        'nice-watch': 'no',
        fit: 'fitted',
        'name-size': '',
    },

    isLoading: false,
    isAddMoreShirt: false,
    setAddMoreShirt: (isAddMoreShirt: boolean) => {
        set({ isAddMoreShirt });
    },
    setIsLoading: (state: boolean) => {
        set({ isLoading: state });
    },
    setElbowPatch: () => {
        const curData = get().activeProductData;
        const activeFabric = get().activeFabric;
        // apply for all
        if (get().isApplyForAll) {
            Object.keys(get().activeProductData).forEach((key) => {
                curData[key].cuff.elbowPatch = !curData[activeFabric].cuff.elbowPatch;
            });
        } else {
            // or not
            curData[activeFabric].cuff.elbowPatch = !curData[activeFabric].cuff.elbowPatch;
        }
        set({ activeProductData: { ...deepCopy(curData) } });
        const procedure = getProcedures();
        if (procedure?.changeElbowPatch) {
            procedure?.changeElbowPatch(
                curData[activeFabric].cuff.elbowPatch,
                curData[activeFabric].cuff.value
            );
        }
    },
    setStitchApply: (state?: boolean) => {
        const curData = get().activeProductData;
        const activeFabric = get().activeFabric;
        const newState = state !== undefined ? state : !curData[activeFabric].stitch.theLastOne;
        // apply for all
        if (get().isApplyForAll) {
            Object.keys(get().activeProductData).forEach((key) => {
                curData[key].stitch.theLastOne = newState;
            });
        } else {
            // or only last button
            curData[activeFabric].stitch.theLastOne = newState;
        }
        set({ activeProductData: { ...deepCopy(curData) } });
        const procedure = getProcedures();
        if (procedure?.changeStitch) {
            if (curData[activeFabric].stitch.theLastOne) {
                procedure?.changeStitch(DEFAULT_STITCH, false);
            }
            procedure?.changeStitch(
                curData[activeFabric].stitch.color,
                curData[activeFabric].stitch.theLastOne
            );
        }
    },
    changeInforStepValues: (
        step: string,
        childState: string | { [key in stepSize]: string },
        childStep: string = ''
    ) => {
        const oldSteps = get().inforStepValues;
        if (childStep) {
            set({
                inforStepValues: {
                    ...oldSteps,
                    size: { ...oldSteps.size, [childStep]: childState },
                },
            });
        } else {
            set({ inforStepValues: { ...oldSteps, [step]: childState } });
        }
    },
    inforInputValues: {
        neck: '',
        chest: '',
        stomach: '',
        hips: '',
        shoulder: '',
        'sleeves-long': '',
        'sleeves-short': '',
        biceps: '',
        wrist: '',
        'front-length': '',
        'phone-number': '',
        'any-remarks': '',
    },
    changeInforInputValues: (id: string, value: string) => {
        const oldValues = get().inforInputValues;
        set({ inforInputValues: { ...oldValues, [id]: value } });
    },
    selectingApplyAllFabric: '',
    changeSelectingApplyAllFabric: (state: string) => {
        set({ selectingApplyAllFabric: state });
    },
    isShowSelectedFabric: false,
    changeShowSelectedFabric: (state: boolean) => {
        set({ isShowSelectedFabric: state });
    },
    isApplyForAll: false,
    changeApplyForAll: (state: boolean) => {
        set({ isApplyForAll: state });
    },
    addNewProduct: () => {
        const currentFabricImage = getImagePath(DEFAULT_FABRIC);
        let curData = get().activeProductData;
        const activeFb = get().activeFabric;
        // click new fabric
        let count = 0;
        Object.keys(curData).forEach(() => {
            count++;
            // const isSameAsOuter = curData[get().activeFabric]['inner-fabric'].isSameAsOuter;
        });
        const randomFabricId = generateFabricId(); // 114;
        if (count < 3) {
            const curInner = curData?.[activeFb]['inner-fabric'];
            const newPlacements = deepCopy(curInner.innerPlacement);
            if (get().isApplyForAll) {
                const outerFabric = curData?.[activeFb].fabric;
                newPlacements &&
                    Object.values(newPlacements).forEach((plc) => {
                        if (plc.isSameAsOuter) {
                            newPlacements[plc['placement-id']].fabricId = outerFabric.value;
                            newPlacements[plc['placement-id']].img = outerFabric.img;
                            newPlacements[plc['placement-id']].isSameAsOuter = true;
                        }
                    });
            }

            curData[randomFabricId] = {
                ...(get().isApplyForAll
                    ? deepCopy(get().activeProductData[get().activeFabric])
                    : deepCopy(INIT_PRODUCT_DATA)),
                fabric: {
                    ...deepCopy(DEFAULT_FABRIC_OBJ),
                    tab: get().activeProductData?.[get().activeFabric].fabric.tab,
                    ...(currentFabricImage && { img: currentFabricImage }),
                },
                'inner-fabric': {
                    ...(get().isApplyForAll
                        ? {
                              ...deepCopy(INIT_PRODUCT_DATA['inner-fabric']),
                              innerPlacement: newPlacements,
                          }
                        : deepCopy(INIT_PRODUCT_DATA['inner-fabric'])),
                },
            };
            if (!get().isApplyForAll) {
                const procedures = getProcedures();
                if (procedures && procedures?.applyNewFabric) {
                    procedures?.applyNewFabric(currentFabricImage || DEFAULT_FABRIC_ID);
                }
                set({ activeFabric: randomFabricId });
            }
            set({ activeProductData: { ...deepCopy(curData) } });
        }
    },
    // fabric for the other fabric follow to (when check apply for all)
    fabricApplied: '',
    changeFabricApplied: (fabric: string) => {
        if (fabric) {
            let curData = get().activeProductData;
            const rootFabric = curData[fabric];
            Object.keys(curData).forEach((key) => {
                if (key !== fabric) {
                    // preserve fabric
                    const oldFabric = curData[key].fabric;
                    const oldInnerFabric = curData[key]['inner-fabric'];
                    const newInnerPlacements = deepCopy(
                        curData[fabric]['inner-fabric'].innerPlacement
                    );
                    newInnerPlacements &&
                        Object.values(newInnerPlacements).forEach((plc) => {
                            if (plc.isSameAsOuter) {
                                newInnerPlacements[plc['placement-id']].fabricId =
                                    curData[key].fabric.value;
                                newInnerPlacements[plc['placement-id']].img =
                                    curData[key].fabric.img;
                            }
                        });
                    curData[key] = {
                        ...rootFabric,
                        fabric: { ...oldFabric },
                        'inner-fabric': {
                            ...oldInnerFabric,
                            innerPlacement: deepCopy(newInnerPlacements),
                            // if is same as outer -> get outer fabric for inner
                        },
                    };
                }
            });
            console.log(curData);
            set({
                // add active fabric
                fabricApplied: fabric,
                activeProductData: { ...deepCopy(curData) },
            });
        } else {
            // change active fabric
            set({ fabricApplied: fabric });
        }
    },

    inforFlow: 'body',
    changeInforFlow: (tab: inforTab) => {
        set({ inforFlow: tab });
    },
    inforStep: 0,
    changeInforStep: (step: number) => {
        if (step === 0) {
            set({ inforFlow: '' });
        }
        set({ inforStep: step });
    },

    setStitch: (value: string, color: string) => {
        const curData = get().activeProductData;
        const activeFabric = get().activeFabric;
        // set stitch for the other fabric
        if (get().isApplyForAll) {
            Object.keys(curData).forEach((key) => {
                curData[key].stitch.value = value;
                curData[key].stitch.color = color;
            });
        } else {
            curData[activeFabric].stitch = {
                value,
                color,
                theLastOne: curData[activeFabric].stitch.theLastOne,
            };
        }
        set({ activeProductData: { ...deepCopy(curData) } });
        const procedures = getProcedures();
        if (procedures?.changeStitch) {
            procedures?.changeStitch(color, curData[activeFabric].stitch.theLastOne);
        }
    },

    setMonogram: (type: string, value: string) => {
        const curData = get().activeProductData;
        const activeFabric = get().activeFabric;
        const activePlacement = curData[activeFabric].monogram.activePlacement;
        const currentMonogram = curData[activeFabric].monogram;
        const procedures = getProcedures();
        // apply for current fabric
        if (type === 'activePlacement') {
            curData[activeFabric].monogram.activePlacement = value;
            if (procedures) {
                procedures?.changeMonogramPosition && procedures?.changeMonogramPosition(value);
            }
        } else if (type === 'isVisible') {
            const newVisibility = !curData[activeFabric].monogram.placements?.[value].isVisible;
            curData[activeFabric].monogram.placements![value].isVisible = newVisibility;
            if (!newVisibility) {
                curData[activeFabric].monogram.placements![value].text = '';
                curData[activeFabric].monogram.placements![value]['style-id'] = 'Arial';
                procedures?.changeMonogramText &&
                    procedures?.changeMonogramText(activePlacement, '');
                procedures?.changeMonogramFont &&
                    procedures?.changeMonogramFont(activePlacement, 'Arial');
            }
        } else if (type === 'text' && value !== undefined) {
            curData[activeFabric].monogram.placements![activePlacement!].text = value.trim();
            if (procedures && procedures?.changeMonogramText) {
                procedures?.changeMonogramText(activePlacement, value.trim());
            }
        } else if (type === 'color') {
            if (activePlacement) {
                curData[activeFabric].monogram.placements![activePlacement]['color'] = value;
            }
            if (procedures && procedures?.changeMonogramColor) {
                procedures?.changeMonogramColor(activePlacement, value);
            }
        } else {
            // style
            if (activePlacement) {
                curData[activeFabric].monogram.placements![activePlacement]['style-id'] = value;
            }
            if (procedures && procedures?.changeMonogramFont) {
                procedures?.changeMonogramFont(activePlacement, value);
            }
        }
        if (get().isApplyForAll) {
            Object.keys(curData).forEach((key) => {
                if (key !== activeFabric) {
                    curData[key].monogram = deepCopy(currentMonogram);
                    curData[key].monogram.activePlacement = '';
                }
            });
        }

        set({ activeProductData: { ...deepCopy(curData) } });
    },
    setInnerFabric: (type: string, value?: string, img?: string) => {
        const curData = get().activeProductData;
        const activeFabric = get().activeFabric;
        const currentInnerFabric = curData[activeFabric]['inner-fabric'];
        const activePlacement = currentInnerFabric.activePlacement;
        const placements = currentInnerFabric.innerPlacement;
        const procedures = getProcedures();
        if (type === 'inner-fabric') {
            let imgSrc;
            if (img) {
                imgSrc = getImagePath(img);
            }
            procedures?.changeInnerFabric && procedures.changeInnerFabric(activePlacement, img);
            if (currentInnerFabric?.innerPlacement) {
                currentInnerFabric.innerPlacement[activePlacement!].fabricId = value!;
                currentInnerFabric.innerPlacement[activePlacement!].img = imgSrc || '';
            }
            if (placements?.[activePlacement!].isSameAsOuter) {
                placements[activePlacement!].isSameAsOuter = false;
            }
        } else if (type === 'as-outer-fabric') {
            // apply for inner fabric
            const isCheck = !placements?.[activePlacement!].isSameAsOuter;
            if (get().isApplyForAll) {
                Object.keys(get().activeProductData).forEach((key) => {
                    Object.values(curData[key]['inner-fabric'].innerPlacement!).forEach(
                        (placement) => {
                            if (placement['placement-id'] === activePlacement) {
                                const curInnerPlacements =
                                    curData[key]['inner-fabric'].innerPlacement;
                                curInnerPlacements![activePlacement].isSameAsOuter = isCheck;
                                if (curInnerPlacements) {
                                    if (isCheck) {
                                        // if check -> assign outer-fabric values for inner-fabrics
                                        curInnerPlacements![activePlacement!].img =
                                            curData[key].fabric.img;
                                        curInnerPlacements![activePlacement!].fabricId =
                                            curData[key].fabric.value;
                                    } else {
                                        // if uncheck -> use default value
                                        curInnerPlacements![activePlacement!].img =
                                            DEFAULT_INNER_IMAGE;
                                        curInnerPlacements![activePlacement!].fabricId =
                                            DEFAULT_INNER_VALUE;
                                    }
                                }
                            }
                        }
                    );
                });
            } else {
                if (currentInnerFabric?.innerPlacement) {
                    currentInnerFabric.innerPlacement[activePlacement!].isSameAsOuter = isCheck;
                    if (isCheck) {
                        // if check
                        currentInnerFabric.innerPlacement[activePlacement!].img =
                            curData[activeFabric].fabric.img;
                        currentInnerFabric.innerPlacement[activePlacement!].fabricId =
                            curData[activeFabric].fabric.value;
                    } else {
                        // if uncheck
                        currentInnerFabric.innerPlacement[activePlacement!].img =
                            DEFAULT_INNER_IMAGE;
                        currentInnerFabric.innerPlacement[activePlacement!].fabricId =
                            DEFAULT_INNER_VALUE;
                    }
                }
            }
            if (procedures?.changeInnerFabric) {
                procedures?.changeInnerFabric &&
                    procedures?.changeInnerFabric(
                        activePlacement,
                        getImagePath(currentInnerFabric.innerPlacement![activePlacement!].img)
                    );
            }
        } else if (type === 'placement') {
            curData[activeFabric]['inner-fabric'].activePlacement = value; // placementId
            if (procedures) {
                procedures?.changeInnerPosition && procedures?.changeInnerPosition(value);
            }
        }
        set({ activeProductData: { ...deepCopy(curData) } });
    },

    resetToDefault: () => {
        const curConfig = get().currentConfig as ProductsDataKeys;
        const curData = { ...deepCopy(get().activeProductData) };
        const procedure = getProcedures();
        const puzzleName = getConfigName(curConfig, 'procedure');
        const initObj = INIT_PRODUCT_DATA[get().currentConfig as ProductsDataKeys];
        if (curConfig) {
            if (procedure?.[puzzleName]) {
                if (curConfig === 'stitch') {
                    procedure?.[puzzleName](INIT_PRODUCT_DATA[curConfig as ProductsDataKeys].color);
                } else if (curConfig === 'inner-fabric') {
                    procedure?.resetInnerFabric();
                    // procedure?.[puzzleName](, getImagePath(DEFAULT_INNER));
                } else if (curConfig === 'fabric') {
                    procedure?.[puzzleName](getImagePath(DEFAULT_FABRIC));
                } else {
                    if (curConfig === 'cuff') {
                        if (procedure?.changeElbowPatch) {
                            procedure?.changeElbowPatch(false);
                        }
                    }
                    procedure?.[puzzleName](INIT_PRODUCT_DATA[curConfig as ProductsDataKeys].value);
                }
            }
            //
            if (curConfig === 'stitch') {
                get().setStitch(DEFAULT_STITCH_NAME, DEFAULT_STITCH);
                get().setStitchApply(false);
            } else if (curConfig === 'monogram') {
                procedure?.resetMonogramText() && procedure?.resetMonogramText();
                const defaultValue = {
                    value: '',
                    activePlacement: '',
                    placements: { ...deepCopy(INIT_PRODUCT_DATA.monogram.placements) },
                };
                if (get().isApplyForAll) {
                    Object.keys(curData).forEach((id) => {
                        curData[id].monogram = { ...deepCopy(defaultValue) };
                    });
                } else {
                    curData[get().activeFabric].monogram = { ...deepCopy(defaultValue) };
                }
                set({ activeProductData: curData });
            } else if (curConfig === 'inner-fabric') {
                const curPlacement = curData[get().activeFabric]['inner-fabric'].activePlacement;
                if (get().isApplyForAll) {
                    Object.keys(curData).forEach((id) => {
                        curData[id]['inner-fabric'] = {
                            ...deepCopy({
                                ...INIT_PRODUCT_DATA['inner-fabric'],
                                ...{
                                    activePlacement:
                                        id === get().activeFabric ? curPlacement : 'inner-collar',
                                },
                            }),
                        };
                    });
                } else {
                    curData[get().activeFabric]['inner-fabric'] = {
                        ...deepCopy({
                            ...INIT_PRODUCT_DATA['inner-fabric'],
                            activePlacement: curPlacement,
                        }),
                    };
                }
                set({ activeProductData: curData });
            } else {
                // except stitch & monogram
                get().setProductFabric(
                    {
                        value: initObj.value,
                        ...(initObj.name && { name: initObj.name }), // for outer fabric
                        ...(initObj.price && { price: initObj.price }), // for outer fabric
                        ...(initObj.elbowPatch !== undefined && {
                            elbowPatch: initObj.elbowPatch,
                        }),
                        ...(initObj.theLastOne !== undefined && {
                            theLastOne: initObj.theLastOne,
                        }),
                        ...(initObj.tab && { tab: initObj.tab }),
                    },
                    curConfig as ProductsDataKeys,
                    initObj.img
                );
            }
        }
    },

    removeSelectedFabric: (fabricId: string) => {
        if (get().fabricApplied !== fabricId) {
            let curData = get().activeProductData;
            let count = 0;
            Object.keys(curData).forEach(() => {
                count++;
            });
            if (count > 1) {
                delete curData[fabricId];
            }
            set({ activeProductData: { ...deepCopy(curData) } });
        }
    },
    // click on options
    // except: monogram, stitch
    setProductFabric: (data: ProductsDataDetail, type, img?: string) => {
        // change active Fabric
        const currentFabricId = get().activeFabric;
        if (type) {
            let curData = get().activeProductData;
            const tab = get().activeProductData?.[currentFabricId]?.[type]?.tab;
            const elbowPatch = get().activeProductData?.[currentFabricId]?.[type]?.elbowPatch;
            // const isSameAsOuter = get().activeProductData?.[currentFabricId]?.[type]?.isSameAsOuter;

            const newData = {
                // don't change the orders
                ...(elbowPatch !== undefined && { elbowPatch }), //1
                ...(tab && { tab }), // 2
                ...data, // 3
                ...(type === 'fabric' && data), // 4
                ...(img && { img }), // 5
            };
            curData[currentFabricId][type] = newData;
            if (type === 'fabric') {
                const procedures = getProcedures();
                const newInnerPlacements = deepCopy(
                    curData[currentFabricId]['inner-fabric'].innerPlacement
                );
                newInnerPlacements &&
                    Object.values(newInnerPlacements).forEach((plc) => {
                        if (plc.isSameAsOuter) {
                            newInnerPlacements[plc['placement-id']].fabricId =
                                curData[currentFabricId].fabric.value;
                            newInnerPlacements[plc['placement-id']].img =
                                curData[currentFabricId].fabric.img;
                            if (procedures?.changeInnerFabric) {
                                procedures?.changeInnerFabric(plc['placement-id'], img);
                            }
                        }
                    });
                curData[currentFabricId]['inner-fabric'].innerPlacement = newInnerPlacements;
                // if (get().isApplyForAll) {
                //     Object.keys(curData).forEach((fabric) => {
                //         let currentPlacements = deepCopy(
                //             curData[fabric]['inner-fabric'].innerPlacement
                //         );
                //         currentPlacements &&
                //             Object.values(currentPlacements).forEach((plc) => {
                //                 if (plc.isSameAsOuter) {
                //                     currentPlacements![plc['placement-id']].fabricId =
                //                         curData[fabric].fabric.value;
                //                     currentPlacements![plc['placement-id']].img =
                //                         curData[fabric].fabric.img;
                //                     if (fabric === currentFabricId) {
                //                         if (procedures?.changeInnerFabric) {
                //                             procedures?.changeInnerFabric(plc['placement-id'], img);
                //                         }
                //                     }
                //                     curData[currentFabricId]['inner-fabric'].innerPlacement =
                //                         currentPlacements;
                //                 }
                //             });
                //     });
                // } else {
                //     const newInnerPlacements = deepCopy(
                //         curData[get().activeFabric]['inner-fabric'].innerPlacement
                //     );
                //     newInnerPlacements &&
                //         Object.values(newInnerPlacements).forEach((plc) => {
                //             if (plc.isSameAsOuter) {
                //                 newInnerPlacements[plc['placement-id']].fabricId =
                //                     curData[currentFabricId].fabric.value;
                //                 newInnerPlacements[plc['placement-id']].img =
                //                     curData[currentFabricId].fabric.img;
                //                 if (procedures?.changeInnerFabric) {
                //                     procedures?.changeInnerFabric(plc['placement-id'], img);
                //                 }
                //             }
                //         });
                //     curData[currentFabricId]['inner-fabric'].innerPlacement = newInnerPlacements;
                // }
            } else {
                if (get().isApplyForAll) {
                    Object.keys(get().activeProductData).forEach((key) => {
                        curData[key][type] = deepCopy(newData);
                    });
                }
            }
            set({
                activeProductData: { ...deepCopy(curData) },
            });
        }
    },

    inforFormState: false,

    changeInforFormState: (state: boolean) => {
        set({
            inforFormState: state,
            currentConfig: '',
        });
        setTimeout(() => {
            set({
                inforFlow: '',
                inforStep: 0,
            });
        }, 250);
        // transition 250ms
    },

    runProcedure: (
        procedureName: string,
        item: ConfigOption,
        type?: ProductsDataKeys,
        src?: string
    ) => {
        const procedures = getProcedures();
        if (procedures && procedures?.[procedureName]) {
            if (type === 'fabric') {
                const material = getImagePath(src || item.id);
                procedures?.[procedureName]?.(material);
            } else if (type === 'inner-fabric') {
                const curData = get().activeProductData;
                const activeFabric = get().activeFabric;
                const activePlacement = curData[activeFabric]['inner-fabric'].activePlacement;
                const material = getImagePath(src || item.id);
                procedures?.[procedureName]?.(activePlacement, material);
            } else {
                procedures?.[procedureName]?.(item.id);
            }
        }
    },

    applyAllCurrentOptions: (fabricId: string) => {
        // apply current state to model (when click to selected fabrics)
        const data = get().activeProductData[fabricId];
        const activeConfig = get().currentConfig;
        if (data) {
            const procedures = getProcedures();
            if (procedures) {
                for (let key in data) {
                    const procedureName = getConfigName(key, 'procedure');
                    const procedure = procedures?.[procedureName];
                    if (key === 'fabric') {
                        const value = data[key as keyof ProductsData].img;
                        const val = getImagePath(value);
                        procedure && procedure(val);
                    } else if (key === 'inner-fabric') {
                        data['inner-fabric'].innerPlacement &&
                            Object.values(data['inner-fabric'].innerPlacement).forEach((plc) => {
                                const img = getImagePath(plc.img);
                                procedure && procedure(plc['placement-id'], img);
                            });
                    } else if (key === 'monogram') {
                        const monogram = data[key as keyof ProductsData];
                        const placements = monogram.placements;
                        if (placements) {
                            for (let plc in placements) {
                                const text = placements[plc].text;
                                const activePlacement = monogram.activePlacement;
                                const placementId = placements[plc]['placement-id'];
                                // change visibility
                                if (activeConfig === 'monogram') {
                                    if (activePlacement) {
                                        procedures?.changeMonogramPosition &&
                                            procedures.changeMonogramPosition(activePlacement);
                                    } else {
                                        procedures?.changeConfigurator &&
                                            procedures.changeConfigurator('monogram');
                                    }
                                }
                                procedures.changeMonogramText &&
                                    procedures.changeMonogramText(placementId, text);
                            }
                        }
                    } else {
                        const value = data[key as keyof ProductsData].value;
                        if (procedure && value) {
                            if (key === 'stitch') {
                                const color = data[key as keyof ProductsData].color;
                                procedure(DEFAULT_STITCH, false);
                                procedure(color, data[key as keyof ProductsData].theLastOne);
                            } else {
                                if (key === 'cuff') {
                                    procedures?.changeElbowPatch &&
                                        procedures?.changeElbowPatch(
                                            data.cuff.elbowPatch,
                                            data.cuff.value
                                        );
                                }
                                procedure(value);
                            }
                        }
                    }
                }
            }
        }
    },

    activeFabric: DEFAULT_FABRIC_ID,

    setActiveFabrics: (activeFabric: string) => {
        get().applyAllCurrentOptions(activeFabric);
        set({ activeFabric });
        if (
            get().currentConfig === 'stitch' &&
            get().activeProductData?.[activeFabric]?.placket?.value === 'hidden-placket'
        ) {
            set({ currentConfig: '' });
        }
    },

    activeProductData: {
        [DEFAULT_FABRIC_ID]: {
            ...deepCopy(INIT_PRODUCT_DATA),
        },
    },

    changeTab: (tab: string) => {
        const curData = get().activeProductData;
        const activeFabric = get().activeFabric;
        const currentConfig = get().currentConfig;
        curData[activeFabric][currentConfig as ProductsDataKeys].tab = tab;
        if (tab === 'short-sleeve' || tab === 'long-sleeve') {
            const procedures = getProcedures();
            if (tab === 'short-sleeve') {
                procedures?.changeConfigurator && procedures?.changeConfigurator('cuff-short');
            } else {
                procedures?.changeConfigurator && procedures?.changeConfigurator('cuff');
            }
        }
        set({ activeProductData: { ...deepCopy(curData) } });
    },

    currentConfig: '',
    setCurrentConfig: (currentConfig: string) => {
        if (get().currentConfig === currentConfig) {
            return set({ currentConfig: '' });
        } else {
            return set({ currentConfig });
        }
    },
}));

export default useStateStore;
